import config from 'config';
import { joinPath } from 'utils/string';

const {
  constants: {
    API: { PRICING_UPDATE_URL, BASE_URL },
  },
} = config;

const SEARCH_CHAT_ENDPOINT = '/ps/integrations/agents/property-search/chat/stream';

/**
 * The frontend-owned `resolve_location` tool (Story 4) intercepts the model's
 * tool call and resolves it itself, via the same `property/suggestionsnew`
 * endpoint the existing searchSuggestions redux action calls — hit directly
 * rather than through that action, since going through the shared `suggestion`
 * reducer state would collide with whatever the visible location search box
 * is concurrently typing/debouncing. Matches the `lookupLocations` precedent
 * on the search-ai-v2 POC branch (src/data/search/actions.js): same bare-fetch
 * approach, and the same `/resource/auth/ps4` prefix the redux action's own
 * `request()` helper adds under the hood (`joinPath('/resource/auth/ps4', path)`).
 *
 * `types` is the endpoint's own address-type filter (propstream-be
 * `LocationService.suggest`): one letter per wanted type out of `PSCNTZ` —
 * P property, S street, C city, N county, T state, Z zip. Omitted, the
 * backend defaults to all of `PSCNTZ`. AI Search passes the scope-bearing
 * subset so single-property/street rows don't crowd out the city/county/
 * state/zip row the agent is actually trying to pick (ATAI-191).
 */
export async function resolveLocationSuggestions(query, types) {
  const q = (query || '').trim();
  if (!q) return [];

  const params = new URLSearchParams({ q });
  if (types) params.set('types', types);

  const url = `${BASE_URL}${joinPath('/resource/auth/ps4', 'property/suggestionsnew')}?${params}`;
  const resp = await fetch(url, {
    headers: { 'x-auth-token': window.appAuthToken },
  });

  if (!resp.ok) throw new Error(`API error ${resp.status}`);

  const body = await resp.json().catch(() => []);
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.items)) return body.items;
  if (body && Array.isArray(body.results)) return body.results;
  return [];
}

/**
 * ATAI-155 (S7 sub-task 4): fetches the last 10 messages (5 user + 5
 * assistant turns, newest first) plus the most recent applied search-state
 * snapshot for the given persistent session id, from ATAI-154's read
 * endpoint (propstream-pricing's AgentChatController — confirmed live,
 * Dev Completed). `X-User-Id` is required server-side but is added by the
 * gateway from the auth token, same as every other agent-chat call here —
 * not something this client sets itself.
 */
export async function fetchSearchAgentChatHistory(sessionId) {
  const url = `${PRICING_UPDATE_URL}/ps/integrations/agents/property-search/chat/history?${new URLSearchParams(
    { sessionId }
  )}`;
  const resp = await fetch(url, {
    headers: { 'x-auth-token': window.appAuthToken },
  });

  if (!resp.ok) throw new Error(`API error ${resp.status}`);

  return resp.json();
}

export const AgentErrorCause = {
  SERVICE_UNAVAILABLE: 'service_unavailable',
  TRUNCATED: 'truncated',
  RATE_LIMITED_DAILY: 'rate_limited_daily',
  RATE_LIMITED_MINUTE: 'rate_limited_minute',
  MAX_ITERATIONS: 'max_iterations',
  PROCESSING_ERROR: 'processing_error',
  UNREACHABLE: 'unreachable',
  INCOMPLETE: 'incomplete',
  UNKNOWN: 'unknown',
};

// `agentCause` rather than Error's own `cause`: the `new Error(msg, { cause })`
// options bag is silently ignored on browsers that predate it, which would
// leave every error looking like UNKNOWN.
function agentError(message, cause) {
  const err = new Error(message);
  err.agentCause = cause;
  return err;
}

/**
 * Streaming client for the Search Assistant agent (S2's property-search chat
 * handler). Mirrors propertyAgentClient.js's SSE parsing — no shared core
 * exists on this branch yet, so the streaming loop is duplicated rather than
 * introducing a new abstraction for a single caller.
 */
export async function streamSearchAgentChat({
  history,
  context,
  sessionId,
  tools,
  signal,
  onText,
  onToolUse,
  onDone,
  onError,
}) {
  let resp;
  try {
    resp = await fetch(`${PRICING_UPDATE_URL}/ps/integrations/agents/property-search/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': window.appAuthToken,
      },
      body: JSON.stringify({
        sessionId,
        context,
        tools,
        messages: history.map((m) => ({
          role: m.role,
          content: m.content,
        })),
      }),
    });
  } catch (e) {
    // never reached the service — offline, DNS, CORS, connection refused
    throw agentError('AI Search is temporarily unavailable.', AgentErrorCause.UNREACHABLE);
  }

  if (!resp.ok) {
    // reached the service but it refused before opening the stream
    const err = await resp.json().catch(() => ({}));
    throw agentError(err.error?.message || `API error ${resp.status}`, AgentErrorCause.UNREACHABLE);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentToolName = null;
  let currentToolInput = '';
  let currentToolId = null;
  let doneFired = false;
  let sawMessageStop = false;
  let errorFired = false;
  const fireDone = () => {
    if (doneFired) return;
    doneFired = true;
    onDone?.();
  };
  const fireError = (message, cause) => {
    errorFired = true;
    onError?.(agentError(message, cause));
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop(); // keep incomplete line in buffer

    for (const line of lines) {
      // SseEmitter wraps each line in "data:" prefix
      const stripped = line.startsWith('data:') ? line.slice(5) : line;
      const trimmed = stripped.trim();
      if (!trimmed) continue;

      // The backend forwards raw Anthropic SSE lines, which themselves have "event:" and "data:" prefixes
      // Parse the inner Anthropic SSE format
      if (trimmed.startsWith('event:')) continue; // we use the data line to determine event type

      let dataStr = trimmed;
      if (dataStr.startsWith('data:')) {
        dataStr = dataStr.slice(5).trim();
      }
      if (!dataStr || dataStr === '[DONE]') continue;

      try {
        const parsed = JSON.parse(dataStr);

        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          onText?.(parsed.delta.text);
        }

        if (parsed.type === 'content_block_start' && parsed.content_block?.type === 'tool_use') {
          currentToolName = parsed.content_block.name;
          currentToolId = parsed.content_block.id;
          currentToolInput = '';
        }

        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'input_json_delta') {
          currentToolInput += parsed.delta.partial_json;
        }

        if (parsed.type === 'content_block_stop' && currentToolName) {
          let args = {};
          try {
            args = JSON.parse(currentToolInput);
          } catch (e) {
            console.error(
              `Failed to parse tool_use input for ${currentToolName}: ${currentToolInput}`
            );
          }
          onToolUse?.(currentToolName, args, currentToolId);
          currentToolName = null;
          currentToolId = null;
          currentToolInput = '';
        }

        if (parsed.type === 'message_stop') {
          sawMessageStop = true;
          fireDone();
        }

        if (parsed.type === 'error') {
          // `cause` is absent on an Anthropic-raised error event forwarded
          // verbatim, and on the controller's own catch-all line
          fireError(
            parsed.error?.message || 'Stream error',
            parsed.error?.cause || AgentErrorCause.UNKNOWN
          );
        }
      } catch (e) {
        // Not JSON — skip (could be a comment or blank line)
      }
    }
  }

  if (!sawMessageStop && !errorFired) {
    // stream closed with no terminal event — emitter timeout or dropped connection
    fireError('The response did not finish.', AgentErrorCause.INCOMPLETE);
  }

  fireDone();
}
